import React from 'react';
import { Box } from '@mui/material';
import { CustomizedDataGrid } from 'components/customized_data_grid';

type SortInfo = {
  sortBy: string;
  sortDir?: string;
};

type PipelineRow = {
  severity?: string;
  bgColor?: string;
  timestamp?: string;
  recurring?: boolean;
  [key: string]: unknown;
};

type FormatterProps = {
  row: PipelineRow;
};

type PipelineColumn = {
  name: string;
  key?: string;
  width?: number;
  formatter?: (props: FormatterProps) => React.ReactNode;
  renderCell?: (props: FormatterProps) => React.ReactNode;
  cellClass?: string;
  headerCellClass?: string;
  sortable?: boolean;
  sortOptions?: string[];
  [key: string]: unknown;
};

interface PipelineStatusTableProps {
  columns: PipelineColumn[];
  rows: PipelineRow[];
  tableType?: string;
  onSorting?: (sortInfo: SortInfo) => void;
  sortingInfo?: SortInfo;
  isFetching?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  isRenderPagination?: boolean;
  isPrevNextPagination?: boolean;
  height?: number | string;
  hideTopBar?: boolean;
  showMoreButton?: boolean;
  moveToolbarAboveTable?: boolean;
  isSortIcon?: boolean;
  hideClearFilterOption?: boolean;
}

const FALLBACK_STATUS_COLOR = '#9E9E9E';
const DEFAULT_NUMERIC_FALLBACK = 10;
const DEFAULT_SORTING_INFO: SortInfo = { sortBy: '', sortDir: '' };

const STATUS_COLOR_MAP: Record<string, string> = {
  Critical: '#f54b3c',
  Major: '#f79227',
  Minor: '#f1aa2c',
  Warning: '#facc32',
  OK: '#37b66e',
};

const TABLE_CONTAINER_STYLES = {
  width: '100%',
  '& .rdg-cell': {
    borderRight: '1px solid rgba(255, 255, 255, 0.2) !important',
    padding: '8px 12px !important',
    display: 'flex',
    alignItems: 'center',
  },
  '& .rdg-header-row .rdg-cell': {
    borderRight: '1px solid rgba(255, 255, 255, 0.2) !important',
    padding: '8px 12px !important',
    '&:last-child': {
      borderRight: 'none !important',
    },
    '& > div, & .filter-icon, & .sort-icon': {
    width: '100% !important',
    display: 'flex !important',
    justifyContent: 'space-between !important',
      gap: '8px !important',
      alignItems: 'center !important',
      height: '100% !important',
    },
    '& span': {
      fontWeight: '600 !important',
      textTransform: 'uppercase !important',
      lineHeight: '1 !important',
      display: 'flex !important',
      alignItems: 'center !important',
    },
  },

  // Hide only the sort icon for HOUR header, keep the HOUR text visible
  '& .hour-header-cell .sort-icon, & .hour-header-cell .filter-icon, & .hour-header-cell svg, & .hour-header-cell [data-testid="sort-icon"], & .hour-header-cell [class*="icon"]': {
    display: 'none !important',
  },

  '& .rdg-row': {
    borderBottom: 'none !important',
  },
} as const;

const createContainerStyles = (
  height: number | string,
  hideTopBar: boolean,
  moveToolbarAboveTable: boolean,
) => {
  const styles: any = {
    ...TABLE_CONTAINER_STYLES,
    height,
    position: 'relative',
    '& > .MuiStack-root': {
      width: '100% !important',
    },
    '& .rdg': {
      width: '100% !important',
      maxWidth: '100% !important',
      maxHeight: 'none !important',
      overflowX: 'auto !important',
      ...(hideTopBar && {
        overflowY: 'auto !important',
      }),
    },
    '& [role="grid"]': {
      width: '100% !important',
      minWidth: '100% !important',
    },
  };

  if (hideTopBar) {
    styles['& > .MuiStack-root'] = {
      display: 'none !important',
    };
  } else if (moveToolbarAboveTable) {
    styles['& > .MuiStack-root'] = {
      width: '100% !important',
      position: 'absolute',
      top: '-54px',
      right: '0',
      zIndex: '2',
      justifyContent: 'flex-end',
      marginBottom: '0 !important',
      pointerEvents: 'auto',
    };
  }

  return styles;
};

const isValidDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !value) {
    return false;
  }
  return !Number.isNaN(new Date(value).getTime());
};

const getDateObject = (timestamp?: string): Date | null => {
  if (!isValidDate(timestamp)) {
    return null;
  }
  return new Date(timestamp);
};

const padWithZero = (value: number): string => String(value).padStart(2, '0');

const formatDateValue = (date: Date): string => {
  const month = padWithZero(date.getMonth() + 1);
  const day = padWithZero(date.getDate());
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

const formatDateTimeValue = (date: Date): string => {
  const formattedDate = formatDateValue(date);
  const hours = padWithZero(date.getHours());
  const minutes = padWithZero(date.getMinutes());
  return `${formattedDate} ${hours}:${minutes}`;
};

const getFormattedDate = (timestamp?: string): string => {
  const date = getDateObject(timestamp);
  if (!date) {
    return timestamp ?? '';
  }
  return formatDateValue(date);
};

const getFormattedDateTime = (timestamp?: string): string => {
  const date = getDateObject(timestamp);
  if (!date) {
    return timestamp ?? '';
  }
  return formatDateTimeValue(date);
};

const getNumericDisplayValue = (
  row: PipelineRow,
  field: string,
  fallback: number = DEFAULT_NUMERIC_FALLBACK,
): number => {
  const rawValue = row[field];
  if (rawValue != null && !Number.isNaN(Number(rawValue))) {
    return Number(rawValue);
  }
  return fallback;
};

const getRecurringDisplayValue = (recurring?: boolean): 'Yes' | 'No' =>
  recurring === true ? 'Yes' : 'No';

const getSeverityColor = (row: PipelineRow): string => {
  if (row.bgColor) {
    return row.bgColor;
  }
  if (row.severity && STATUS_COLOR_MAP[row.severity]) {
    return STATUS_COLOR_MAP[row.severity];
  }
  return FALLBACK_STATUS_COLOR;
};

export const StatusChip: React.FC<FormatterProps> = ({ row }) => {
  const value = row.severity?.toUpperCase() || '';
  const backgroundColor = getSeverityColor(row);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.5,
        py: 0.5,
        backgroundColor,
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '24px',
        borderRadius: '4px',
        minWidth: '70px',
        width: '100%',
        textAlign: 'center',
      }}
    >
      {value}
    </Box>
  );
};

export const renderStatusFormatter = ({ row }: FormatterProps) => <StatusChip row={row} />;

export const renderDateFormatter = ({ row }: FormatterProps) => (
  <span>{getFormattedDate(row.timestamp)}</span>
);

export const renderTimeFormatter = ({ row }: FormatterProps) => (
  <span>{getFormattedDateTime(row.timestamp)}</span>
);

export const renderNumberFormatter =
  (field: string) =>
  ({ row }: FormatterProps) =>
    <span>{getNumericDisplayValue(row, field)}</span>;

export const renderRecurringFormatter = ({ row }: FormatterProps) => (
  <span>{getRecurringDisplayValue(row.recurring)}</span>
);

const createColumn = (column: PipelineColumn): PipelineColumn => column;

const normalizeColumns = (columns: PipelineColumn[]): PipelineColumn[] =>
  columns.map((column) => {
    const normalizedName = column.name?.toUpperCase?.() ?? '';
    const isHourColumn = normalizedName === 'HOUR' || column.key === 'hour';

    if (isHourColumn) {
      return {
        ...column,
        minWidth: column.minWidth ?? 100,
        sortable: false,
        sortOptions: [],
        headerCellClass: 'hour-header-cell',
      };
    }

    const estimatedMinWidth = (column.name?.length || 0) * 8 + 52;

    return {
      ...column,
      minWidth: column.minWidth ?? Math.max(estimatedMinWidth, 120),
      sortOptions: ['Sort Asc - Desc', 'Sort Dec - Asc'],
    };
  });

export const DEFAULT_PIPELINE_TABLE_COLUMNS: PipelineColumn[] = [
  createColumn({
    key: 'initial_event_time',
    name: 'DATE',
    width: 150,
    formatter: renderDateFormatter,
  }),
  createColumn({
    key: 'hour',
    name: 'HOUR',
    width: 100,
    formatter: renderNumberFormatter('hour'),
    sortable: false,
    sortOptions: [],
    headerCellClass: 'hour-header-cell',
  }),
  createColumn({ key: 'domain', name: 'JOB TYPE', width: 120 }),
  createColumn({ key: 'number', name: 'JOB ID', width: 150 }),
  createColumn({ key: 'source', name: 'JOB NAME', width: 200 }),
  createColumn({
    key: 'mappedSeverity',
    name: 'STATUS',
    width: 150,
    renderCell: StatusChip,
    formatter: renderStatusFormatter,
    cellClass: 'status-center',
  }),
  createColumn({
    key: 'initial_event_time',
    name: 'START TIME',
    width: 150,
    formatter: renderTimeFormatter,
  }),
  createColumn({
    key: 'initial_event_time',
    name: 'END TIME',
    width: 150,
    formatter: renderTimeFormatter,
  }),
  createColumn({ key: 'program', name: 'PROGRAM', width: 150 }),
  createColumn({
    key: 'runTime',
    name: 'RUN TIME MIN',
    width: 160,
    formatter: renderNumberFormatter('runTime'),
  }),
  createColumn({
    key: 'expectedRunTime',
    name: 'EXPECTED RUN TIME MIN',
    width: 230,
    formatter: renderNumberFormatter('expectedRunTime'),
  }),
  createColumn({
    key: 'avgRunTime',
    name: 'AVG RUN TIME MIN',
    width: 200,
    formatter: renderNumberFormatter('avgRunTime'),
  }),
  createColumn({
    key: 'totalRecords',
    name: 'TOTAL RECORDS',
    width: 180,
    formatter: renderNumberFormatter('totalRecords'),
  }),
  createColumn({
    key: 'recurring',
    name: 'RECURRING',
    width: 150,
    formatter: renderRecurringFormatter,
  }),
  createColumn({
    key: 'recurrencesPerDay',
    name: 'RECURRENCES PER DAY',
    width: 230,
    formatter: renderNumberFormatter('recurrencesPerDay'),
  }),
  createColumn({ key: 'triggerName', name: 'TRIGGER NAME', width: 150 }),
  createColumn({ key: 'triggerType', name: 'TRIGGER TYPE', width: 150 }),
  createColumn({ key: 'dataAssetName', name: 'DATA ASSET NAME', width: 150 }),
  createColumn({ key: 'silverTableName', name: 'SILVER TABLE NAME', width: 150 }),
  createColumn({ key: 'bronzeTableName', name: 'BRONZE TABLE NAME', width: 150 }),
  createColumn({ key: 'subscriptionName', name: 'SUBSCRIPTION NAME', width: 150 }),
  createColumn({ key: 'subscriptionId', name: 'SUBSCRIPTION ID', width: 150 }),
  createColumn({ key: 'resourceGroup', name: 'RESOURCE GROUP', width: 150 }),
];

export const DEFAULT_PIPELINE_COL_NAMES = [
  'DATE',
  'HOUR',
  'JOB TYPE',
  'JOB ID',
  'JOB NAME',
  'STATUS',
  'START TIME',
  'END TIME',
  'PROGRAM',
  'RUN TIME MIN',
  'EXPECTED RUN TIME MIN',
  'AVG RUN TIME MIN',
  'TOTAL RECORDS',
  'RECURRING',
  'RECURRENCES PER/DAY',
];

const noop = (): void => undefined;
const noopFilterHandler = (): void => undefined;

const PipelineStatusTable: React.FC<PipelineStatusTableProps> = ({
  columns,
  rows,
  tableType = 'PipelineStatus',
  onSorting,
  sortingInfo = DEFAULT_SORTING_INFO,
  isFetching = false,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isRenderPagination = false,
  isPrevNextPagination = false,
  height = 'auto',
  hideTopBar = false,
  showMoreButton,
  moveToolbarAboveTable = false,
  isSortIcon = false,
  hideClearFilterOption = false,
}) => {
  const handlePageChange = onPageChange
    ? (_: unknown, nextPage: number) => onPageChange(nextPage)
    : undefined;

  type PageSizeChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;

  const handlePageSizeChange: PageSizeChangeHandler | undefined = onPageSizeChange
    ? (event) => onPageSizeChange(Number(event.target.value))
    : undefined;

  const finalColumns = React.useMemo(() => normalizeColumns(columns), [columns]);

  return (
    <Box sx={createContainerStyles(height, hideTopBar, moveToolbarAboveTable)}>
      <CustomizedDataGrid
        title=""
        tableColumns={finalColumns}
        tableData={rows}
        tableType={tableType}
        height={height}
        defaultColumnNames={finalColumns.map((column) => column.name)}
        rowHeight={50}
        headerRowHeight={50}
        onSorting={onSorting ?? noop}
        onFilterCheck={noopFilterHandler}
        onFilterClick={noopFilterHandler}
        onFilterSearch={noopFilterHandler}
        currSorting={sortingInfo}
        currFilter={{}}
        isFetching={isFetching}
        listLength={total}
        page={page}
        paginationData={
          total != null && pageSize != null
            ? { totalElements: total, totalPages: Math.ceil(total / pageSize) }
            : undefined
        }
        pageSize={pageSize}
        handlePageChange={handlePageChange}
        handlepageSizeChange={handlePageSizeChange}
        isPrevNextPagination = {isPrevNextPagination}
        isShowMoreButton={showMoreButton ?? (!isRenderPagination && !hideTopBar)}
      />
    </Box>
  );
};

export default PipelineStatusTable;
